import React from "react";
import { useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, Zap, Smartphone, ShieldCheck, IdCard, PenLine, QrCode,
  Stethoscope, Scale, HardHat, Ticket, Clock, UserCheck, Building2,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1512428559087-560fa5ceab42?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxwZXJzb24lMjB1c2luZyUyMHNtYXJ0cGhvbmUlMjBtb2JpbGUlMjBjaGVjayUyMGlufGVufDB8fHx8MTc4ODQwMzQwM3ww&ixlib=rb-4.1.0&q=85";
const SIGN_IMG = "https://images.unsplash.com/photo-1670852714979-f73d21652a83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwc2lnbmF0dXJlJTIwdGFibGV0JTIwYnVzaW5lc3N8ZW58MHx8fHwxNzg4NDAzNDAzfDA&ixlib=rb-4.1.0&q=85";

const INDUSTRIES = [
  {
    icon: Stethoscope,
    title: "Healthcare & Dental Practices",
    who: "Urgent cares, outpatient clinics, dental practices, physical therapy centers, and med-spas.",
    pain: "Patients arrive 15 minutes early to fill out clipboards — delaying provider schedules and crowding the lobby.",
    value: "Patients complete check-in and snap photos of their ID and insurance cards on their phones before they ever walk in.",
  },
  {
    icon: Scale,
    title: "Professional & Legal Services",
    who: "Personal injury law firms, accounting firms, wealth management offices, and real estate brokerages.",
    pain: "High drop-off during onboarding when heavy PDF retainer agreements are mailed or emailed back and forth.",
    value: "High-trust, mobile-first e-signatures and ID verification, sent by text instantly while the prospect is still on the phone.",
  },
  {
    icon: HardHat,
    title: "Field Operations & Industrial Services",
    who: "Oilfield service providers, equipment rentals, HVAC/plumbing fleets, and logistics hubs.",
    pain: "Paper tickets, inspection sheets, and delivery acknowledgments get lost or take days to re-key.",
    value: "Field workers or customers sign off on a smartphone, instantly triggering backend billing and job tracking.",
  },
  {
    icon: Ticket,
    title: "High-Volume Consumer & Venues",
    who: "Skydiving and adventure parks, auto dealerships (test drives), gyms and fitness studios, and event venues.",
    pain: "Long queues at the registration desk for liability waivers and check-ins.",
    value: "A QR code at the door or a text link lets guests Blitz through waivers in about 30 seconds.",
  },
];

const HOW = [
  { icon: Smartphone, title: "Send a link or QR code", body: "Text a secure link or post a QR code at your door. No app to download." },
  { icon: PenLine, title: "They Blitz through it", body: "Forms, ID & insurance photos, and e-signatures — all completed on their phone in seconds." },
  { icon: Zap, title: "It lands in your system", body: "Completed, signed records appear instantly — ready for your dashboard, chart, or billing." },
];

const PLAN_FEATURES = [
  "Unlimited patients, clients & forms",
  "Mobile check-in with ID & insurance photo capture",
  "Legally-binding e-signatures (draw or type)",
  "Upload your own PDF — AI builds the form for you",
  "Your logo & brand color on every screen and PDF",
  "QR codes & text links for instant check-in",
  "Staff dashboard, downloadable PDFs & submission alerts",
  "One login for clients across every Blitz business",
];

export default function Landing() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#0F52BA]">
              <Zap className="h-3.5 w-3.5" /> Intake, check-in, waivers & e-signatures
            </span>
            <h1 className="mt-5 text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Skip the clipboard.
              <span className="text-[#0F52BA]"> Blitz the check-in.</span>
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-600 sm:text-lg">
              BlitzEntry lets your customers complete forms, snap their ID and insurance cards, and sign —
              all from their phone before they arrive. Fewer clipboards, shorter lines, and clean data
              that lands straight in your system.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {/* Businesses path */}
              <div className="rounded-2xl border border-blue-100 bg-blue-50/60 p-5" data-testid="path-blitzers">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0F52BA] text-white"><Building2 className="h-5 w-5" /></span>
                <p className="mt-3 text-sm font-bold uppercase tracking-wider text-[#0F52BA]">Create a Nimbus Account</p>
                <p className="mt-1 text-sm text-slate-600">Run a business? Collect paperwork, IDs & signatures before clients arrive.</p>
                <Button className="mt-4 h-11 w-full bg-[#0F52BA] text-sm hover:bg-[#0B3D8A]" onClick={() => navigate("/register")} data-testid="hero-business-start">
                  Start free for 14 days <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
              {/* Users path */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="path-users">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white"><UserCheck className="h-5 w-5" /></span>
                <p className="mt-3 text-sm font-bold uppercase tracking-wider text-slate-700">For BLITZER <span className="text-green-600">· Always FREE</span></p>
                <p className="mt-1 text-sm text-slate-600">One profile for every Blitz business you visit. Check in from your phone.</p>
                <Button variant="outline" className="mt-4 h-11 w-full border-slate-300 text-sm" onClick={() => navigate("/register?type=user")} data-testid="hero-user-start">
                  Create your account <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="mt-4 text-sm text-slate-400">
              Already have an account?{" "}
              <button className="font-semibold text-[#0F52BA] hover:underline" onClick={() => navigate("/login")} data-testid="hero-sign-in">Sign in</button>
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.15 }} className="relative">
            <img src={HERO_IMG} alt="Patient completing check-in on a phone" className="aspect-[4/5] w-full rounded-2xl object-cover shadow-2xl shadow-blue-900/10" />
            <div className="absolute -bottom-5 -left-5 hidden rounded-xl border border-slate-100 bg-white p-4 shadow-xl sm:block">
              <p className="text-sm font-semibold text-slate-900">30-second check-in</p>
              <p className="mt-1 text-xs text-slate-500">No app. No paper. No line.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pain strip */}
      <section className="border-y border-slate-100 bg-slate-50">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-4 py-10 text-center sm:px-6">
          <Clock className="h-7 w-7 text-[#0F52BA]" />
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">The clipboard is quietly costing you</h2>
          <p className="max-w-2xl text-base leading-relaxed text-slate-600">
            Every paper form means early arrivals, crowded lobbies, delayed schedules, drop-offs, and hours of
            re-keying illegible handwriting. BlitzEntry moves all of it to the one device everyone already has.
          </p>
        </div>
      </section>

      {/* Industries */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Built for the businesses that live at the front desk</h2>
          <p className="mt-4 text-base text-slate-600">Wherever people have to sign in, sign forms, or sign off — BlitzEntry replaces the paper.</p>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {INDUSTRIES.map((ind, i) => (
            <motion.div
              key={ind.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="group rounded-2xl border border-slate-200 bg-white p-7 transition-shadow hover:shadow-xl hover:shadow-blue-900/5"
              data-testid={`industry-card-${i}`}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0F52BA]/10 text-[#0F52BA]">
                <ind.icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-xl font-semibold tracking-tight text-slate-900">{ind.title}</h3>
              <p className="mt-2 text-sm font-medium text-slate-500">{ind.who}</p>
              <div className="mt-5 space-y-3 border-t border-slate-100 pt-5 text-sm leading-relaxed">
                <p className="text-slate-600"><span className="font-semibold text-slate-900">The problem: </span>{ind.pain}</p>
                <p className="text-slate-600"><span className="font-semibold text-[#0F52BA]">With BlitzEntry: </span>{ind.value}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-slate-100 bg-slate-50">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Three steps. Zero paper.</h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {HOW.map((s, i) => (
              <div key={s.title} className="rounded-2xl border border-slate-200 bg-white p-7" data-testid={`how-step-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0F52BA] text-sm font-bold text-white">{i + 1}</span>
                  <s.icon className="h-5 w-5 text-[#0F52BA]" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* One login value */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#0F52BA]">
              <UserCheck className="h-3.5 w-3.5" /> One profile, everywhere
            </span>
            <h2 className="mt-5 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">One login for every Blitz business</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Every client and patient gets their own secure account, and their information is saved for next time.
              When they visit another business on BlitzEntry, they get one-click entry — no re-typing their name,
              address, ID, or insurance ever again.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                { icon: ShieldCheck, t: "Secure personal account with saved details" },
                { icon: IdCard, t: "ID & insurance stored once, reused instantly" },
                { icon: Building2, t: "One-click entry at any BlitzEntry company" },
              ].map((f) => (
                <li key={f.t} className="flex items-center gap-3 text-sm text-slate-700">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#0F52BA]/10 text-[#0F52BA]"><f.icon className="h-4 w-4" /></span>
                  {f.t}
                </li>
              ))}
            </ul>
          </div>
          <img src={SIGN_IMG} alt="Signing on a tablet" className="aspect-[4/3] w-full rounded-2xl object-cover shadow-xl shadow-blue-900/10" />
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t border-slate-100 bg-slate-50" id="pricing">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Simple, all-in pricing</h2>
            <p className="mt-4 text-base text-slate-600">One plan. Everything included. Cancel anytime.</p>
          </div>
          <div className="mx-auto mt-12 max-w-md">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl shadow-blue-900/5" data-testid="pricing-card">
              <div className="bg-[#0F52BA] px-8 py-8 text-center text-white">
                <p className="text-sm font-semibold uppercase tracking-wider text-blue-100">Nimbus Pro</p>
                <div className="mt-3 flex items-end justify-center gap-1">
                  <span className="text-5xl font-bold" data-testid="price-amount">$99</span>
                  <span className="mb-1.5 text-blue-100">/ month</span>
                </div>
                <p className="mt-2 text-sm text-blue-100">per business location</p>
              </div>
              <div className="px-8 py-8">
                <ul className="space-y-3.5">
                  {PLAN_FEATURES.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0F52BA]" /> {f}
                    </li>
                  ))}
                </ul>
                <Button size="lg" className="mt-8 h-12 w-full bg-[#0F52BA] text-base hover:bg-[#0B3D8A]" onClick={() => navigate("/register")} data-testid="pricing-cta">
                  Start your 14-day free trial
                </Button>
                <p className="mt-3 text-center text-xs text-slate-400">No credit card to start · Subscribe anytime to keep your portal active</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-4 py-16 text-center sm:px-6">
        <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Ready to clear your lobby?</h2>
        <p className="mx-auto mt-4 max-w-xl text-base text-slate-600">Set up your business in minutes and send your first Blitz link today.</p>
        <Button size="lg" className="mt-8 h-12 bg-[#0F52BA] px-8 text-base hover:bg-[#0B3D8A]" onClick={() => navigate("/register")} data-testid="final-cta">
          Get started <ArrowRight className="ml-1.5 h-5 w-5" />
        </Button>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-4 py-8 text-sm text-slate-400 sm:flex-row sm:px-6">
          <span>© {new Date().getFullYear()} BlitzEntry · Secure digital intake, check-in & e-signatures</span>
          <span>For healthcare, legal, field ops & venues</span>
        </div>
      </footer>
    </div>
  );
}
